/**
 * GeohashSubscriptionManager
 *
 * Shared utility for managing Firestore geohash-band subscriptions.
 * Extracted from TilesProvider to avoid code duplication in GridCellsProvider.
 *
 * Usage:
 *   const manager = new GeohashSubscriptionManager();
 *   manager.subscribe(bounds, collectionRef, onSnapshot);
 *   manager.disconnect();
 */

import {
    collection,
    Firestore,
    limit,
    onSnapshot,
    orderBy,
    query,
    startAt,
    endAt,
    Unsubscribe,
} from 'firebase/firestore';

export type SnapshotHandler = (snapshot: any) => void;

export class GeohashSubscriptionManager {
    private subscriptions: Unsubscribe[] = [];

    /**
     * Subscribe to all geohash bands for a given set of bounds.
     * Each band becomes one onSnapshot subscription.
     *
     * @param bounds      - Array of [startHash, endHash] pairs from geohashQueryBounds
     * @param firestore   - Firestore instance
     * @param collectionName - Firestore collection name
     * @param onData      - Called for each snapshot
     * @param onError     - Called on subscription error (optional)
     */
    subscribe(
        bounds: [string, string][],
        firestore: Firestore,
        collectionName: string,
        onData: SnapshotHandler,
        onError?: (err: Error) => void,
        queryLimit?: number,
    ): void {
        for (const [start, end] of bounds) {
            const constraints = queryLimit
                ? [orderBy('geohash'), startAt(start), endAt(end), limit(queryLimit)]
                : [orderBy('geohash'), startAt(start), endAt(end)];
            const q = query(collection(firestore, collectionName), ...constraints);
            const unsub = onSnapshot(
                q,
                onData,
                (err) => {
                    console.error(`[GeohashSubscriptionManager] snapshot error on ${collectionName}:`, err);
                    onError?.(err);
                },
            );
            this.subscriptions.push(unsub);
        }
    }

    /**
     * Unsubscribe all active listeners.
     */
    disconnect(): void {
        for (const unsub of this.subscriptions) {
            unsub();
        }
        this.subscriptions = [];
    }

    get activeCount(): number {
        return this.subscriptions.length;
    }
}
